import { useState, useRef, useEffect } from 'react';
import { FaLayerGroup, FaTools, FaBookOpen, FaCaretDown, FaCheck } from 'react-icons/fa';
import { SHOW_TRANSIT_SCENARIO } from '../config/features';

// The nine features are three different kinds of thing — overlays you switch on,
// tools you work in, and the paper's case studies — so they are grouped rather
// than laid out as one flat row that outgrows the bar.
function Menu({ label, icon: Icon, items, openId, setOpenId, id }) {
    const ref = useRef(null);
    const open = openId === id;
    const activeCount = items.filter((i) => i.active).length;

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpenId(null); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open, setOpenId]);

    return (
        <div className="nav-menu" ref={ref}>
            <button
                className={`data-button ${activeCount ? 'active' : ''}`}
                onClick={() => setOpenId(open ? null : id)}
            >
                <Icon size={14} color={activeCount ? '#7CC644' : '#000'} />
                <span>{label}</span>
                {activeCount > 0 && <span className="nav-count">{activeCount}</span>}
                <FaCaretDown size={12} color={activeCount ? '#7CC644' : '#000'} />
            </button>
            {open && (
                <div className="nav-dropdown">
                    {items.map((i) => (
                        <button
                            key={i.label}
                            className={`nav-item ${i.active ? 'active' : ''}`}
                            onClick={() => { i.onClick && i.onClick(); setOpenId(null); }}
                            disabled={!i.onClick || !!i.disabled}
                            title={i.disabled || undefined}
                        >
                            <span className="nav-item-check">{i.active && <FaCheck size={10} />}</span>
                            <span>{i.label}</span>
                            {i.disabled && <span className="nav-item-why">{i.disabled}</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function DataTypeSelection({
    onSelectDataType, showShortestPath, onToggleShortestPath, showFacilities, onToggleFacilities,
    showMacroAnalysis, onToggleMacroAnalysis, showSchoolZones, onToggleSchoolZones,
    showCongestionPricing, onToggleCongestionPricing, showNetworkEditor, onToggleNetworkEditor,
    showPublicTransport, onTogglePublicTransport, showIncome, onToggleIncome,
    showTransitInvestment, onToggleTransitInvestment,
    disabledReason, editorReason, lockReason, hideEditor, hidePolicies, bayArea,
}) {
    const [openId, setOpenId] = useState(null);

    // Everything that reads a produced run is off on a metro that has none; the
    // Network Editor, which runs the chain itself, stays on.
    const layers = [
        { label: 'Traffic Density', onClick: () => onSelectDataType('Traffic Density'),
          disabled: disabledReason },
        bayArea && { label: 'Public Transport', active: showPublicTransport, onClick: onTogglePublicTransport },
        bayArea && { label: 'Low-income households (below 80% of Bay Area median income)',
          active: showIncome, onClick: onToggleIncome },
        { label: 'Accidents' },          // not implemented yet
        { label: 'Facilities', active: showFacilities, onClick: onToggleFacilities },
    ].filter(Boolean);
    // Tools = act on the map now and hit a live service (Valhalla, the SUMO
    // edit service). Case Studies = prepared analyses you read.
    const tools = [
        { label: 'Shortest Path', active: showShortestPath, onClick: onToggleShortestPath },
        !hideEditor && { label: 'Network Editor', active: showNetworkEditor,
          onClick: onToggleNetworkEditor, disabled: editorReason },
    ].filter(Boolean);
    const caseStudies = hidePolicies ? [] : [
        { label: 'School Zones', active: showSchoolZones, onClick: onToggleSchoolZones,
          disabled: disabledReason },
        { label: 'Congestion Pricing', active: showCongestionPricing,
          onClick: onToggleCongestionPricing, disabled: disabledReason },
        bayArea && SHOW_TRANSIT_SCENARIO && { label: 'Transit Investment', active: showTransitInvestment,
          onClick: onToggleTransitInvestment },
    ].filter(Boolean);

    // A metro being loaded takes out every entry at once, including the ones that
    // are otherwise always on: nothing here works against a half-held metro.
    const gate = (items) =>
        (lockReason ? items.map((i) => ({ ...i, disabled: lockReason })) : items);

    return (
        <div className="data-type-selection">
            <div className="filter-left">
                <Menu id="layers" label="Layers" icon={FaLayerGroup} items={gate(layers)}
                    openId={openId} setOpenId={setOpenId} />
                <Menu id="tools" label="Tools" icon={FaTools} items={gate(tools)}
                    openId={openId} setOpenId={setOpenId} />
                {caseStudies.length > 0 && (
                    <Menu id="cases" label="Case Studies" icon={FaBookOpen} items={gate(caseStudies)}
                        openId={openId} setOpenId={setOpenId} />
                )}
            </div>
        </div>
    );
}

export default DataTypeSelection;
